import React from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { AlertCircle, X } from "lucide-react";

import PlatformAppBar from "../../../components/layout/PlatformAppBar";
import PlatformSidebar from "../../../components/layout/homebuyer/PlatformSidebar";
import PlatformFooter from "../../../components/layout/PlatformFooter";

import "./HomebuyerLayout.css";
import "../account/HomebuyerAccountPage.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const DOSSIER_META_STORAGE_KEY = "legup_hb_dossier_meta";

function resolveDossierPath(summary) {
  try {
    const rawMeta = localStorage.getItem(DOSSIER_META_STORAGE_KEY);
    const savedDraftMeta = rawMeta ? JSON.parse(rawMeta) : null;

    const hasDossier = Boolean(summary?.has_dossier);
    const hasDraftDossier = Boolean(summary?.has_draft_dossier);
    const hasReadyForExportDossier = Boolean(
      summary?.has_ready_for_export_dossier,
    );
    const hasExportedDossier = Boolean(summary?.has_exported_dossier);

    if (hasDraftDossier) {
      return summary?.current_step_path || "/hb/dossier/form/personal-info";
    }

    if (hasDossier || hasReadyForExportDossier || hasExportedDossier) {
      if (summary?.dossier_status === "ACTION_REQUIRED_FROM_HB") {
        return summary?.current_step_path || "/hb/dossier/form/personal-info";
      }

      return "/hb/dossier";
    }

    if (savedDraftMeta?.current_step_path) {
      return savedDraftMeta.current_step_path;
    }

    return "/hb/dossier";
  } catch (error) {
    console.error("Could not resolve dossier path:", error);
    return "/hb/dossier";
  }
}

export default function HomebuyerLayout() {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const navigate = useNavigate();

  const [me, setMe] = React.useState(null);
  const [isLoadingMe, setIsLoadingMe] = React.useState(true);
  const [meError, setMeError] = React.useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const [accountDirty, setAccountDirty] = React.useState(false);
  const [pendingPath, setPendingPath] = React.useState(null);
  const [leaveAccountModalOpen, setLeaveAccountModalOpen] =
    React.useState(false);

  function requestNavigation(path, state = null) {
    const currentPath = window.location.pathname;

    if (currentPath === path) return;

    if (currentPath === "/hb/account" && accountDirty) {
      setPendingPath({ path, state });
      setLeaveAccountModalOpen(true);
      return;
    }

    navigate(path, state ? { state } : undefined);
  }

  const loadMe = React.useCallback(async () => {
    if (!isAuthenticated) {
      setMe(null);
      setIsLoadingMe(false);
      return;
    }

    try {
      setIsLoadingMe(true);
      setMeError("");

      const token = await getAccessTokenSilently();

      const res = await fetch(`${API_BASE}/api/homeowner/me/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        console.error("Failed to load /api/homeowner/me/", data);

        setMeError("Failed to load dashboard data.");

        setMe(null);
        return;
      }

      if (data?.linked === false) {
        setMe(null);
        setMeError("");
        return;
      }

      setMe(data);
    } catch (error) {
      console.error("Failed to load /api/homeowner/me/", error);
      setMeError("Failed to load dashboard data.");
      setMe(null);
    } finally {
      setIsLoadingMe(false);
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  React.useEffect(() => {
    loadMe();
  }, [loadMe]);

  const account = me?.account || {};
  const profile = me?.profile || {};
  const dashboardSummary = me?.dashboard_summary ?? null;

  const dossierPath = React.useMemo(() => {
    return resolveDossierPath(dashboardSummary);
  }, [dashboardSummary]);

  const fullName =
    account.full_name ||
    `${account.first_name || ""} ${account.last_name || ""}`.trim() ||
    "User";

  const initials =
    account.initials ||
    `${account.first_name?.[0] || ""}${account.last_name?.[0] || ""}`.toUpperCase() ||
    "U";

  function cancelLeaveAccount() {
    setLeaveAccountModalOpen(false);
    setPendingPath(null);
  }

  function confirmLeaveAccount() {
    sessionStorage.removeItem("legup_hb_account_form_draft");

    setAccountDirty(false);
    setLeaveAccountModalOpen(false);

    if (pendingPath?.path) {
      navigate(
        pendingPath.path,
        pendingPath.state ? { state: pendingPath.state } : undefined,
      );
    }

    setPendingPath(null);
  }

  return (
    <div className="hb-dashboard-page">
      <PlatformAppBar
        actorType={account?.primary_actor}
        fullName={fullName}
        initials={initials}
        homePath="/"
        onOpenMobileMenu={() => setMobileMenuOpen(true)}
        avatarImageUrl={profile.profile_image_url}
        avatarFit="cover"
      />

      <div className="hb-dashboard-main">
        <PlatformSidebar
          mobileOpen={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          dashboardSummary={{
            ...dashboardSummary,
            dossier_path: dossierPath,
          }}
          onNavigateRequest={requestNavigation}
          notificationsEnabled={
            isAuthenticated && !isLoadingMe && Boolean(me?.account)
          }
        />

        <div className="hb-dashboard-content-shell">
          {!me && isLoadingMe ? (
            <div
              className="hb-layout-loading"
              role="status"
              aria-label="Loading"
            >
              <div className="hb-layout-loader">
                <span className="hb-layout-loader__ring" />

                <span className="hb-layout-loader__dot" />
              </div>
            </div>
          ) : meError && !me ? (
            <div className="hb-layout-loading">
              <div className="hb-layout-loading__card hb-layout-loading__card--error">
                {meError}
              </div>
            </div>
          ) : (
            <Outlet
              context={{
                me,
                account,
                profile,
                dashboardSummary,
                dossierPath,
                refreshMe: loadMe,
                isLoadingMe,
                setAccountDirty,
              }}
            />
          )}
        </div>
      </div>

      {leaveAccountModalOpen ? (
        <div
          className="hb-account-confirm-overlay"
          onClick={cancelLeaveAccount}
        >
          <div
            className="hb-account-confirm"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="hb-account-confirm__icon">
              <AlertCircle size={22} />
            </div>

            <div className="hb-account-confirm__content">
              <h3>Leave without saving?</h3>
              <p>
                You have unsaved account changes. If you leave now, they will be
                lost.
              </p>

              <div className="hb-account-confirm__actions">
                <button
                  type="button"
                  className="hb-account-confirm__cancel"
                  onClick={cancelLeaveAccount}
                >
                  Stay
                </button>

                <button
                  type="button"
                  className="hb-account-confirm__confirm"
                  onClick={confirmLeaveAccount}
                >
                  Leave
                </button>
              </div>
            </div>

            <button
              type="button"
              className="hb-account-confirm__close"
              onClick={cancelLeaveAccount}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      ) : null}

      <PlatformFooter />
    </div>
  );
}
